import { Field } from 'react-final-form';
import styled, { css } from 'styled-components/macro';
import * as validators from 'validators';
import { CalculatorFormValues } from 'common/types';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { combineValidators } from 'validators/combineValidators';
import * as formFormatters from '@net0-calc/common/form-utils/formatters';
import * as formParsers from '@net0-calc/common/form-utils/parsers';
import { Input, RadioButton, StepDescription, StepTitle } from '@net0-calc/common/components/ui';
import { WizardPageProps } from '@net0-calc/common/types';
import { DEVICE } from '@net0-calc/common/assets/styles';
import { HotelDistribution } from './HotelDistribution';

const translationPrefix = 'events.pages.hospitality.form.field';

export const HospitalityPage: FC<WizardPageProps<CalculatorFormValues>> = ({ values, form }) => {
  const { t } = useTranslation();

  const totalGuests =
    (values?.livingLocallyPeopleCount || 0) +
    (values?.travelingWithinRegionPeopleCount || 0) +
    (values?.travelingWithinCountryPeopleCount || 0) +
    (values?.travelingFromAnotherCountryPeopleCount || 0);

  const validateNotExceedTotalGuests = (value?: number) => {
    if (!value || totalGuests === 0) return undefined;
    return value > totalGuests ? t('formValidationErrors.exceedsTotalGuests', { value: totalGuests }) : undefined;
  };

  const eventDates = values?.eventDurationDates;
  const maxHotelNights = (() => {
    if (!eventDates?.[0] || !eventDates?.[1]) return 0;
    const start = new Date(eventDates[0]);
    const end = new Date(eventDates[1]);
    const eventNights = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    return eventNights + 2; // +2 buffer for arriving early / leaving late
  })();

  const validateHotelNights = (value?: number) => {
    if (!value || maxHotelNights === 0) return undefined;
    return value > maxHotelNights
      ? t('formValidationErrors.exceedsEventDuration', { value: maxHotelNights })
      : undefined;
  };

  const resetPercentNumberOfPeoples = () => {
    form?.change('percentageOfPeopleInFiveStarHotel', undefined);
    form?.change('percentageOfPeopleInFourStarHotel', undefined);
    form?.change('percentageOfPeopleInThreeStarHotel', undefined);
    form?.change('percentageOfPeopleInTwoStarHotel', undefined);
  };

  useEffect(() => {
    if (!values?.stayingInHotelsPeopleCount && values?.knowTheApproximateNumberOfPeople) {
      form?.change('knowTheApproximateNumberOfPeople', undefined);
      resetPercentNumberOfPeoples();
    }
  }, [values?.knowTheApproximateNumberOfPeople, values?.stayingInHotelsPeopleCount, form]);

  return (
    <Root>
      <StepTitle>{t('events.pages.hospitality.title')}</StepTitle>
      <StepDescription>{t('events.pages.hospitality.subTitle')}</StepDescription>

      <FormWrap>
        <InputGroup $hasStayingPeople={!!values?.stayingInHotelsPeopleCount}>
          <Field
            name="stayingInHotelsPeopleCount"
            type="number"
            pattern="[0-9]*"
            parse={formParsers.toNumber}
            format={formFormatters.numberToString}
            validate={combineValidators(
              validators.isNumber,
              validators.isNegativeNumber,
              validateNotExceedTotalGuests,
            )}>
            {field => (
              <StyledInput
                label={t(translationPrefix + '.numberOfPeopleStayingInHotels.label')}
                placeholder="0"
                {...field}
              />
            )}
          </Field>
          <Field
            name="hotelNightsPerAttendeeCount"
            type="number"
            pattern="[0-9]*"
            parse={formParsers.toNumber}
            format={formFormatters.numberToString}
            validate={combineValidators(validators.isNumber, validators.isNegativeNumber, validateHotelNights)}>
            {field => (
              <StyledInput
                label={t(translationPrefix + '.hotelNightsPerAttendeeCount.label')}
                placeholder="0"
                {...field}
              />
            )}
          </Field>
        </InputGroup>

        {!!values?.stayingInHotelsPeopleCount && (
          <Field name="knowTheApproximateNumberOfPeople" type="checkbox">
            {({ input }) => (
              <StyledRadioButton
                text={t(translationPrefix + '.knowTheApproximateNumberOfPeople.label')}
                {...input}
                onChange={value => {
                  resetPercentNumberOfPeoples();
                  input.onChange(value);
                }}
              />
            )}
          </Field>
        )}
        {values?.knowTheApproximateNumberOfPeople && !!values?.stayingInHotelsPeopleCount && (
          <HotelDistribution values={values} form={form} />
        )}
      </FormWrap>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  margin-bottom: 60px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
  }
`;

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div<{ $hasStayingPeople: boolean }>`
  ${({ $hasStayingPeople }) =>
    $hasStayingPeople
      ? css`
          margin-bottom: 20px;

          @media ${DEVICE.laptop} {
            margin-bottom: 50px;
          }
        `
      : ''}
`;

const StyledInput = styled(Input)`
  white-space: nowrap;

  &:last-child {
    & > div {
      margin-bottom: 0;
    }
  }
`;

const StyledRadioButton = styled(RadioButton)`
  ${({ checked }) =>
    checked
      ? css`
          margin-bottom: 32px;

          @media ${DEVICE.laptop} {
            margin-bottom: 50px;
          }
        `
      : 'margin-bottom: 0;'}

  & > label {
    align-items: flex-start;
  }
`;
